import { useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';
import {
  CasesIdentityVerificationV1ApplicationDetailsContainerIdentityVerificationCaseV1Document,
  CasesIdentityVerificationV1ApplicationDetailsContainerIdentityVerificationCaseV1UpdateDocument,
  CasesIdentityVerificationV1ApplicationDetailsContainerStaffUserCurrentDocument,
  IdentityVerificationCaseV1,
  IdentityVerificationCaseV1UpdateInput
} from '../../../../../../generated';
import { useOid } from '../../../../../../hooks/useOid';
import { Country } from '../../../../../../types/country';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled } from '../../../../../shared/utils/queryOptions';
import { ApplicationDetails, ApplicationFormValueProps } from './application-details';
import { lookupCountryQuery } from './identity-verification-v1-country-query';

interface ApplicationDetailsContainerProps {
  caseId: string;
  isCaseOrApplicant: string;
}

export const ApplicationDetailsContainer: React.FC<ApplicationDetailsContainerProps> = (props) => {
  const oid = useOid();
const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled(oid);

  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(lookupCountryQuery);
  const {
    data: caseData,
    loading: caseLoading,
    error: caseError
  } = useQuery(CasesIdentityVerificationV1ApplicationDetailsContainerIdentityVerificationCaseV1Document, {
    variables: {
      identityVerificationCaseV1Id: props.caseId
    }
  });

  const {
    data: staffUserData,
    loading: staffUserLoading,
    error: staffUserError
  } = useQuery(CasesIdentityVerificationV1ApplicationDetailsContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);

  const [saveApplication, { loading: updateLoading }] = useMutation(CasesIdentityVerificationV1ApplicationDetailsContainerIdentityVerificationCaseV1UpdateDocument, {
    refetchQueries: [
      {
        query: CasesIdentityVerificationV1ApplicationDetailsContainerIdentityVerificationCaseV1Document,
        variables: {
          identityVerificationCaseV1Id: props.caseId
        }
      }
    ],
    awaitRefetchQueries: true
  });

  const handleSave = async (data: ApplicationFormValueProps) => {
    const inputData: IdentityVerificationCaseV1UpdateInput = {
      id: props.caseId,
      caseDetails: {
        application: {
          passportDetails: {
            passportCountry: data.passportCountry,
            passportExpiresAt: data.passportExpiresAt,
            passportIssuedAt: data.passportIssuedAt,
            passportNumber: data.passportNumber
          }
        }
      },
      caseName: data.caseName
    };

    if (data?.archiveEligibleAt) {
      inputData.archiveEligibleAt = data?.archiveEligibleAt;
    }
    if (data?.purgeEligibleAt && caseData?.identityVerificationCaseV1?.state === 'draft') {
      inputData.purgeEligibleAt = data?.purgeEligibleAt;
    }
    if (data?.expirationEligibleAt) {
      inputData.expirationEligibleAt = data?.expirationEligibleAt || null;
    }

    return saveApplication({
      variables: {
        input: inputData
      }
    });
  };

  const handleOnUploadSuccess = async (uploadedData: AzureBlobResponse, fieldName: string) => {
    const assetName = fieldName === 'photo' ? 'photoAssets' : 'passportAssets';
    const { blobUrl, versionId } = uploadedData;

    const input: IdentityVerificationCaseV1UpdateInput = {
      id: props.caseId,
      caseDetails: {
        application: {
          [assetName]: {
            [`${fieldName}`]: blobUrl,
            [`${fieldName}Version`]: versionId
          }
        }
      }
    };
    await saveApplication({
      variables: {
        input
      }
    }).then((res) => {
      if (res.data?.identityVerificationCaseV1Update.status.success) {
        message.success('File uploaded successfully');
      } else {
        message.error('Error uploading file: ' + res.data?.identityVerificationCaseV1Update.status.errorMessage);
      }
    });
  };

  return (
    <ComponentQueryLoader
      error={caseError || countryError || staffUserError}
      loading={caseLoading || countryLoading || staffUserLoading}
      hasData={caseData && countryData?.countries?.eventPayload?.countries && staffUserData?.staffUserCurrent?.role?.roleSubtype}
      hasDataComponent={
        <ApplicationDetails
          data={caseData?.identityVerificationCaseV1 as IdentityVerificationCaseV1}
          onSaveApplication={handleSave}
          countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          staffUserType={staffUserData?.staffUserCurrent?.role?.roleSubtype as string}
          caseState={caseData?.identityVerificationCaseV1?.state ?? ''}
          isSavingApplication={updateLoading}
          onUploadSuccess={handleOnUploadSuccess}
          isCurrentAssigned={caseData?.identityVerificationCaseV1?.isCurrentUserAssigned ?? false}
          isCaseOrApplicant={props.isCaseOrApplicant}
        />
      }
    />
  );
};
